import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

import { ConfirmModal } from '~/components/ConfirmModal';
import Button from '~/components/core/Atomic/Buttons/Button';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import { reportAxiosError } from '~/Utils';

import ExternalToolConfigDialog from './ExternalToolConfigDialog';
import ExternalToolConfigsTable from './ExternalToolConfigsTable';
import ResponseSchemaDialog from './ResponseSchemaDialog';
import TEXTS from './texts';
import type { AvailableTool, ConfigFormValues, ExternalToolConfig } from './types';
import { formValuesToCreateRequest, formValuesToUpdateRequest } from './types';

const ExternalToolApisTab: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const baseUrl = `/sysconfig/api/v1/organizations/${organization.id}/external_tool_configs`;

  const [configs, setConfigs] = useState<ExternalToolConfig[]>([]);
  const [availableTools, setAvailableTools] = useState<AvailableTool[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  // Track the row being edited by its immutable tool_name, not a config snapshot, so the dialog
  // always reflects the current `configs` row — e.g. after a failed toggle reverts is_enabled.
  const [editedToolName, setEditedToolName] = useState<string | null>(null);
  // tool_name whose response schema is being previewed (separate read-only dialog), or null.
  const [previewedToolName, setPreviewedToolName] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [configToDelete, setConfigToDelete] = useState<ExternalToolConfig | null>(null);
  // Tools with an in-flight write PUT (a row toggle OR an edit-dialog save). Both target the same
  // `${baseUrl}/${tool_name}` resource, so they must be mutually exclusive per tool to avoid
  // overlapping/out-of-order writes: the ref is the synchronous source of truth, the state disables
  // that row's switch.
  const writingToolsRef = useRef<Set<string>>(new Set());
  const [writingTools, setWritingTools] = useState<Set<string>>(new Set());

  const beginWrite = useCallback((toolName: string) => {
    writingToolsRef.current.add(toolName);
    setWritingTools((prev) => new Set(prev).add(toolName));
  }, []);
  const endWrite = useCallback((toolName: string) => {
    writingToolsRef.current.delete(toolName);
    setWritingTools((prev) => {
      const next = new Set(prev);
      next.delete(toolName);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const [listResponse, toolsResponse] = await Promise.all([
          axios.get<ExternalToolConfig[]>(baseUrl),
          axios.get<AvailableTool[]>(`${baseUrl}/available_tools`),
        ]);
        if (cancelled) return;
        setConfigs(listResponse.data ?? []);
        setAvailableTools(toolsResponse.data ?? []);
      } catch (error) {
        if (cancelled) return;
        setIsError(true);
        reportAxiosError(error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  const existingToolNames = useMemo(() => configs.map((config) => config.tool_name), [configs]);
  const editedConfig = useMemo(
    () => (editedToolName === null ? null : (configs.find((c) => c.tool_name === editedToolName) ?? null)),
    [configs, editedToolName]
  );
  // tool_names whose available-tool entry carries a resolvable response schema — the only rows for
  // which the schema-preview button is enabled.
  const toolsWithSchema = useMemo(
    () => new Set(availableTools.filter((tool) => tool.response_json_schema).map((tool) => tool.tool_name)),
    [availableTools]
  );
  const previewedTool = useMemo(
    () => (previewedToolName === null ? null : (availableTools.find((t) => t.tool_name === previewedToolName) ?? null)),
    [availableTools, previewedToolName]
  );
  // A write (row toggle) for the edited tool is in flight, so a save would hit the same-resource
  // guard in `handleSubmit` and silently no-op. Surface that by disabling Save until it clears.
  const isEditedToolWriting = editedToolName !== null && writingTools.has(editedToolName);

  const openCreate = () => {
    setEditedToolName(null);
    setDialogOpen(true);
  };
  const openEdit = (config: ExternalToolConfig) => {
    setEditedToolName(config.tool_name);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditedToolName(null);
  };

  const handleSubmit = async (values: ConfigFormValues) => {
    // Both an edit-dialog save (PUT) and a create (POST) target the per-tool resource, and an
    // edit save shares that resource with a row toggle. Guard on the tool name so a write already
    // in flight bails out any further submit — Formik's Form still fires onSubmit on Enter even
    // while Save is disabled, so this synchronous ref is what stops a duplicate POST/PUT. tool_name
    // is required by validation, so it's always present here (form value on create, the row on edit).
    const savingToolName = editedConfig?.tool_name ?? values.tool_name;
    if (writingToolsRef.current.has(savingToolName)) return;
    beginWrite(savingToolName);
    setIsSaving(true);
    try {
      if (editedConfig) {
        // tool_name is immutable and addresses the resource in the URL path.
        const { data: updated } = await axios.put<ExternalToolConfig>(
          `${baseUrl}/${editedConfig.tool_name}`,
          formValuesToUpdateRequest(values)
        );
        // Update the row from the mutation response instead of refetching the whole list: a refetch
        // failure would flip the tab into its error state and hide a config that was saved fine.
        setConfigs((prev) => prev.map((c) => (c.tool_name === updated.tool_name ? updated : c)));
      } else {
        const { data: created } = await axios.post<ExternalToolConfig>(baseUrl, formValuesToCreateRequest(values));
        setConfigs((prev) => [...prev, created]);
      }
      closeDialog();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsSaving(false);
      endWrite(savingToolName);
    }
  };

  const handleToggleEnabled = async (config: ExternalToolConfig, isEnabled: boolean) => {
    // Ignore a new toggle while a write for this tool (another toggle or an edit-dialog save) is
    // still in flight, so overlapping requests can't race and leave the server out of sync.
    if (writingToolsRef.current.has(config.tool_name)) return;
    beginWrite(config.tool_name);
    // Optimistically flip the toggle so it feels instantaneous; revert on failure.
    setConfigs((prev) => prev.map((c) => (c.tool_name === config.tool_name ? { ...c, is_enabled: isEnabled } : c)));
    try {
      // The PUT replaces the whole config and every field below is required, so the row's stored
      // values are echoed back alongside the flipped flag — an omission is a 400, not a partial update.
      await axios.put(`${baseUrl}/${config.tool_name}`, {
        url: config.url,
        payload_fields: config.payload_fields,
        query_params: config.query_params,
        is_enabled: isEnabled,
      });
    } catch (error) {
      setConfigs((prev) => prev.map((c) => (c.tool_name === config.tool_name ? { ...c, is_enabled: !isEnabled } : c)));
      reportAxiosError(error);
    } finally {
      endWrite(config.tool_name);
    }
  };

  const handleConfirmDelete = async () => {
    if (!configToDelete) return;
    try {
      await axios.delete(`${baseUrl}/${configToDelete.tool_name}`);
      // Drop the row locally instead of reloading the whole list.
      setConfigs((prev) => prev.filter((c) => c.tool_name !== configToDelete.tool_name));
      setConfigToDelete(null);
    } catch (error) {
      reportAxiosError(error);
    }
  };

  return (
    <div className="flex min-h-[50vh] flex-col gap-16 bg-white p-24">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-4">
          <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
            {TEXTS.tabTitle}
          </Text>
          <Text variant={Text.VARIANTS.SM}>{TEXTS.tabSubtitle}</Text>
        </div>
        <Button color="primary" startIcon={<AddIcon />} onClick={openCreate}>
          {TEXTS.addConfig}
        </Button>
      </div>

      {isLoading || isError ? (
        <SkeletonTable rowsCount={3} columnsCount={6} isError={isError} />
      ) : (
        <ExternalToolConfigsTable
          configs={configs}
          onEdit={openEdit}
          onDelete={setConfigToDelete}
          onPreviewSchema={(config) => setPreviewedToolName(config.tool_name)}
          onToggleEnabled={handleToggleEnabled}
          writingTools={writingTools}
          toolsWithSchema={toolsWithSchema}
        />
      )}

      <ExternalToolConfigDialog
        open={dialogOpen}
        editedConfig={editedConfig}
        availableTools={availableTools}
        existingToolNames={existingToolNames}
        isSaving={isSaving}
        isToolWriting={isEditedToolWriting}
        onSubmit={handleSubmit}
        onClose={closeDialog}
      />

      {previewedTool && <ResponseSchemaDialog tool={previewedTool} onClose={() => setPreviewedToolName(null)} />}

      {configToDelete && (
        <ConfirmModal
          isOpen
          title={TEXTS.deleteConfirm.title}
          contentText={TEXTS.deleteConfirm.body(configToDelete.tool_title)}
          primaryButtonName={TEXTS.deleteConfirm.primary}
          secondaryButtonName={TEXTS.deleteConfirm.secondary}
          onPrimaryBtnClick={handleConfirmDelete}
          onClose={() => setConfigToDelete(null)}
        />
      )}
    </div>
  );
};

export default ExternalToolApisTab;
